import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Bold,
  Check,
  Highlighter,
  Italic,
  List,
  ListOrdered,
  Redo2,
  Underline,
  Undo2,
} from "lucide-react";
import { sanitizeHtml } from "../../lib/richText";
import BottomSheet from "./BottomSheet";
import CustomDropdown from "./CustomDropdown";
import { useIsMobile } from "./useIsMobile";

/**
 * A box for writing formatted text, with the formatting on a bar above it.
 *
 * It is a `contenteditable` element driven through `document.execCommand`,
 * and the value it takes and hands back is HTML. Nothing is installed for it:
 * every browser this app runs in still carries the command set, and what the
 * bar offers - a block style, the inline marks, two kinds of list and two
 * colours - is exactly what that set does well. The markup it makes is
 * cleaned by `sanitizeHtml` on the way in, and whatever draws it again is
 * expected to clean it on the way out too; see `RichTextContent`.
 *
 * The bar offers exactly what the server keeps - see `richText.ts` for the
 * list. There is no size, alignment, strikethrough or link on it on purpose:
 * the server strips each of those on Save without saying so, and a button
 * whose work vanishes on Save is worse than no button.
 *
 * Controlled, the way a text field is: `value` is what the box shows, and
 * every edit comes back through `onChange`. The box only rewrites itself when
 * `value` differs from what it already holds, which is what keeps the caret
 * where it was through an ordinary keystroke - the change came from the box,
 * so the box already has it.
 */

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  /** Shown in the empty box, the way a text field's is. */
  placeholder?: string;
  /** Names the box to a screen reader - "Guidelines", "Policy". */
  label: string;
  /**
   * Layout for the box's outer frame - a height, or `flex-1 min-h-0` for a
   * column that hands its height down. The frame itself is always the card
   * with the bar pinned to its top.
   */
  className?: string;
  /** Layout for the writing area - its minimum height, mostly. */
  contentClassName?: string;
  /**
   * Drawn between the writing area and the bottom of the frame - a Save row,
   * pinned under the text it saves.
   */
  footer?: React.ReactNode;
}

/** What a block can be. `formatBlock` takes the tag; the list shows the word. */
const BLOCKS = [
  { value: "p", label: "Paragraph" },
  { value: "h1", label: "Heading 1" },
  { value: "h2", label: "Heading 2" },
  { value: "h3", label: "Heading 3" },
];

/**
 * The colours the swatch offers. The first two are the app's own text
 * colours; the rest are a spread that reads well on white, with the brand's
 * two teals where a heading is most likely to want them. Anything else is a
 * click away on the custom picker under them.
 */
const COLOURS = [
  { value: "#1E1F24", label: "Ink" },
  { value: "#62636C", label: "Grey" },
  { value: "#00C2CB", label: "Teal" },
  { value: "#007E8C", label: "Deep teal" },
  { value: "#16A34A", label: "Green" },
  { value: "#2563EB", label: "Blue" },
  { value: "#8563DB", label: "Violet" },
  { value: "#DB2777", label: "Pink" },
  { value: "#FF3B30", label: "Red" },
  { value: "#F76B15", label: "Orange" },
  { value: "#CA8A04", label: "Amber" },
  { value: "#92400E", label: "Brown" },
];

/**
 * The colours the highlighter offers: pale, so the words stay legible over
 * them, and a "none" to take a highlight off again. No custom picker - a
 * highlight is a marker pen, and a marker pen comes in a handful of colours.
 */
const HIGHLIGHTS = [
  { value: "#FFF176", label: "Yellow" },
  { value: "#B9F6CA", label: "Green" },
  { value: "#B2EBF2", label: "Cyan" },
  { value: "#F8BBD0", label: "Pink" },
  { value: "#FFE0B2", label: "Orange" },
  { value: "#E1BEE7", label: "Lavender" },
  { value: "#E0E0E0", label: "Grey" },
];

/** What `backColor` answers for a word with no highlight on it. */
const NO_HIGHLIGHT = "transparent";

/** The commands that write a colour; every other one writes a tag. */
const COLOUR_COMMANDS = new Set(["foreColor", "hiliteColor", "backColor"]);

/** What the bar shows as lit, read back from the selection. */
interface Formatting {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  bullets: boolean;
  numbers: boolean;
  block: string;
  colour: string;
  highlight: string;
}

const REST: Formatting = {
  bold: false,
  italic: false,
  underline: false,
  bullets: false,
  numbers: false,
  block: "p",
  colour: "#1E1F24",
  highlight: NO_HIGHLIGHT,
};

/**
 * `rgb(30, 31, 36)` as `#1e1f24`, which is what a colour input takes and what
 * the swatch is compared against. Anything the browser answers in another
 * form is handed back as it came.
 */
const toHex = (colour: string): string => {
  // A fully transparent answer is no colour at all - what the browser says
  // of a word with nothing behind it - and must not be read as black.
  if (/^rgba\(\d+,\s*\d+,\s*\d+,\s*0\)$/.test(colour)) return NO_HIGHLIGHT;
  const match = colour.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) return colour;
  return (
    "#" +
    match
      .slice(1, 4)
      .map((part) => Number(part).toString(16).padStart(2, "0"))
      .join("")
  );
};

// A press on the bar must not take focus out of the box: the selection
// would go with it, and there would be nothing for the command to act on.
const keepFocus = (event: React.MouseEvent) => event.preventDefault();

interface ColourPickerProps {
  title: string;
  /** What the button wears above its colour bar. */
  icon: React.ReactNode;
  colours: { value: string; label: string }[];
  /** The colour under the caret, as a hex string or `NO_HIGHLIGHT`. */
  current: string;
  onPick: (colour: string) => void;
  /** Offers a "none" swatch that picks `NO_HIGHLIGHT`, under this name. */
  none?: string;
  /** Offers the native picker under the swatches, for any colour at all. */
  custom?: boolean;
}

/**
 * A button wearing the colour the caret is standing in, and under it a
 * palette. One component for both the text colour and the highlighter: the
 * two differ only in their swatches and in what the button wears.
 */
const ColourPicker: React.FC<ColourPickerProps> = ({
  title,
  icon,
  colours,
  current,
  onPick,
  none,
  custom = false,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Which of the two shells below the palette is drawn in.
  const isMobile = useIsMobile();

  // The palette closes on a click anywhere else, the way a dropdown does.
  // Not on a phone: the sheet is portalled out of `ref`, so a press on a
  // swatch reads as a press outside and would close the sheet on the way
  // down - taking the swatch off the screen before the click that picks it
  // had landed. The sheet closes itself on a press beside it, which is that
  // same job done by the shell that owns it.
  useEffect(() => {
    if (!open || isMobile) return;
    const close = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open, isMobile]);

  const pick = (colour: string) => {
    onPick(colour);
    setOpen(false);
  };

  const swatch = (
    value: string,
    label: string,
    selected: boolean,
    children?: React.ReactNode
  ) => (
    <button
      key={value}
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={selected}
      onMouseDown={keepFocus}
      onClick={() => pick(value)}
      // Filling its column of the sheet's grid on a phone, which is what
      // makes it a target a thumb can hit; the small square the bar has room
      // for from `md`.
      className="relative flex h-10 w-full items-center justify-center rounded-[6px] border border-black/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 md:h-6 md:w-6 md:rounded-[4px]"
      style={{ backgroundColor: value }}
    >
      {children}
      {selected && (
        <Check aria-hidden="true" className="h-3.5 w-3.5 text-white drop-shadow" />
      )}
    </button>
  );

  /**
   * The swatches, and under them the custom picker where there is one. Built
   * once and drawn in whichever shell the screen calls for, so the phone's
   * palette and the desktop's are never two palettes that can drift apart.
   */
  const palette = (
    <>
      <div className="grid grid-cols-6 gap-2 md:gap-1.5">
        {none &&
          swatch(
            NO_HIGHLIGHT,
            none,
            current === NO_HIGHLIGHT,
            // A diagonal through an empty swatch: the mark for "none".
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-[5px] bg-[linear-gradient(to_top_right,transparent_calc(50%-1px),#FF3B30_calc(50%-1px),#FF3B30_calc(50%+1px),transparent_calc(50%+1px))] md:rounded-[3px]"
            />
          )}
        {colours.map((colour) =>
          swatch(
            colour.value,
            colour.label,
            colour.value.toLowerCase() === current.toLowerCase()
          )
        )}
      </div>
      {/* Any colour at all. The native picker takes focus, which is what
          the editor's saved range is for: the colour is put back on the
          words that were selected before it opened. */}
      {custom && (
        <label className="mt-3 flex cursor-pointer items-center gap-2 text-[13px] text-ink-muted md:text-[12px]">
          <input
            type="color"
            value={current === NO_HIGHLIGHT ? "#000000" : current}
            onChange={(event) => onPick(event.target.value)}
            className="h-10 w-12 cursor-pointer rounded border border-line bg-white p-0 md:h-6 md:w-8"
          />
          Custom colour
        </label>
      )}
    </>
  );

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        type="button"
        title={title}
        aria-label={title}
        aria-haspopup="true"
        aria-expanded={open}
        onMouseDown={keepFocus}
        onClick={() => setOpen((was) => !was)}
        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[6px] border border-line bg-white transition-colors hover:border-[#B8BFC7] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 md:h-7 md:w-7"
      >
        {/* The mark a word processor uses: the icon, and under it a bar in
            the colour the caret is standing in - dashed where there is
            none. */}
        <span aria-hidden="true" className="flex flex-col items-center">
          {icon}
          <span
            className={`mt-[3px] h-[3px] w-[15px] rounded-sm ${
              current === NO_HIGHLIGHT ? "border border-dashed border-[#B8BFC7]" : ""
            }`}
            style={{ backgroundColor: current }}
          />
        </span>
      </button>

      {open &&
        (isMobile ? (
          // Up from the bottom edge on a phone, rather than hung off a button
          // in a bar that scrolls sideways: a panel positioned inside that bar
          // is clipped by the bar's own scroller, and one pinned to the top of
          // the screen is read at arm's length from the thumb picking out of
          // it. Nothing is lost to the sheet taking focus - the command is put
          // back on the range the box was left at. See `range`.
          <BottomSheet open title={title} onClose={() => setOpen(false)}>
            <div className="px-5 pb-1">{palette}</div>
          </BottomSheet>
        ) : (
          <div
            role="group"
            aria-label={title}
            className="absolute left-0 top-full z-20 mt-1.5 w-[188px] rounded-md border border-line bg-white p-3 shadow-[0px_4px_12px_0px_#0000000F]"
          >
            {palette}
          </div>
        ))}
    </div>
  );
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
  label,
  className = "",
  contentClassName = "min-h-[320px]",
  footer,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [formatting, setFormatting] = useState<Formatting>(REST);
  const [empty, setEmpty] = useState(true);
  const id = useId();

  /**
   * Where the caret was the last time it was inside the box.
   *
   * Pressing anything on the bar takes focus - a dropdown opens, a colour
   * picker opens - and the browser drops the selection with it. The command
   * is put back on this range before it runs, so a colour picked from a
   * picker still lands on the words that were selected before it opened.
   */
  const range = useRef<Range | null>(null);

  // What the bar shows is read off the selection rather than remembered from
  // the last press: a caret moved into a bold word lights the button.
  const readFormatting = useCallback(() => {
    const editor = editorRef.current;
    const selection = document.getSelection();
    if (!editor || !selection || selection.rangeCount === 0) return;
    // A DOM without the command set - a test's - has nothing to read.
    if (typeof document.queryCommandState !== "function") return;

    const current = selection.getRangeAt(0);
    if (!editor.contains(current.commonAncestorContainer)) return;
    range.current = current.cloneRange();

    const block = document
      .queryCommandValue("formatBlock")
      .toLowerCase()
      .replace(/[<>]/g, "");

    setFormatting({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      bullets: document.queryCommandState("insertUnorderedList"),
      numbers: document.queryCommandState("insertOrderedList"),
      block: BLOCKS.some((option) => option.value === block) ? block : "p",
      colour: toHex(document.queryCommandValue("foreColor")) || REST.colour,
      highlight: toHex(document.queryCommandValue("backColor")) || NO_HIGHLIGHT,
    });
  }, []);

  useEffect(() => {
    document.addEventListener("selectionchange", readFormatting);
    return () => document.removeEventListener("selectionchange", readFormatting);
  }, [readFormatting]);

  // The box takes the value only when it does not already hold it - see the
  // note on the component. `useLayoutEffect`, so a value that arrives from
  // outside (Cancel, a reset, a switch to the other document) is in the box
  // before it is painted rather than a frame after.
  useLayoutEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    if (editor.innerHTML !== value) editor.innerHTML = sanitizeHtml(value);
    setEmpty((editor.textContent ?? "").trim() === "");
  }, [value]);

  const emit = () => {
    const editor = editorRef.current;
    if (!editor) return;
    setEmpty((editor.textContent ?? "").trim() === "");
    onChange(editor.innerHTML);
  };

  /**
   * How the browser is to write what is typed, and it depends on what is
   * being written. A colour goes as `style` on a `<span>` - the one shape
   * the server keeps a colour in - rather than as a `<font>` tag. Bold,
   * italic and underline go the other way, as `<b>`, `<i>` and `<u>`: with
   * `styleWithCSS` on they would be written as `font-weight` and the like on
   * a span, and the server strips every span style but the two colours, so
   * every mark would come off on Save. A new line is a `<p>` rather than
   * Chrome's `<div>` - the markup the stylesheet and the sanitiser both
   * expect, and the same markup the stored text is written in, so a
   * paragraph typed under it is drawn like the ones above it.
   *
   * Settings on the document rather than on the box, so they are put again
   * each time the box is reached for. Guarded, for a DOM without them.
   */
  const prepare = (command?: string) => {
    if (typeof document.execCommand !== "function") return;
    document.execCommand(
      "styleWithCSS",
      false,
      command && COLOUR_COMMANDS.has(command) ? "true" : "false"
    );
    document.execCommand("defaultParagraphSeparator", false, "p");
  };

  /** Runs one command on the words that were selected, and reports the box. */
  const exec = (command: string, argument?: string) => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();
    const selection = document.getSelection();
    if (range.current && selection) {
      selection.removeAllRanges();
      selection.addRange(range.current);
    }

    prepare(command);
    document.execCommand(command, false, argument);
    emit();
    readFormatting();
  };

  const handleFocus = () => {
    prepare();
    readFormatting();
  };


  // A paste brings the other page's markup with it. Only the text is taken,
  // and the box's own formatting is what it gets.
  const handlePaste = (event: React.ClipboardEvent) => {
    event.preventDefault();
    const text = event.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
    emit();
  };

  const markButton = (
    icon: React.ReactNode,
    title: string,
    active: boolean,
    onPress: () => void
  ) => (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      onMouseDown={keepFocus}
      onClick={onPress}
      // 36px on a phone, where this is a target for a thumb rather than for a
      // pointer, and the bar's own 28px from `md`. The glyph inside is the
      // same size either way - what grows is the room around it.
      className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[6px] border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 md:h-7 md:w-7 ${
        active
          ? "border-[#5BD3E1] bg-[#E6FAFB] text-[#007E8C]"
          : "border-line bg-white text-ink hover:border-[#B8BFC7]"
      }`}
    >
      {icon}
    </button>
  );

  return (
    <div
      className={`flex flex-col rounded-md border border-line bg-white ${className}`}
    >
      {/* The bar. Its controls keep the box focused - see `keepFocus` - so
          a press formats the selection rather than dropping it. */}
      <div
        role="toolbar"
        aria-label={`${label} formatting`}
        aria-controls={id}
        // One row that scrolls sideways on a phone, which is where a phone's
        // own editors keep their formatting: wrapped, these twelve controls
        // take three rows off a 360px screen and push the writing itself below
        // the fold. From `md` it wraps again, and the palettes hang off their
        // own buttons instead of arriving as sheets - which is why the
        // scroller stops there too, a popover inside an `overflow-x-auto` bar
        // being clipped by the bar it is in.
        className="hide-scrollbar flex flex-shrink-0 items-center gap-1.5 overflow-x-auto rounded-t-md border-b border-line bg-[#FAFBFC] px-3 py-2 md:flex-wrap md:gap-1 md:overflow-visible md:py-2.5"
      >
        {/* The two lists take focus as they open, which is fine: the command
            they run is put back on the saved range - see `range`. */}
        <div className="w-[116px] flex-shrink-0">
          <CustomDropdown
            aria-label="Block style"
            size="sm"
            plainSelection
            options={BLOCKS}
            value={formatting.block}
            onChange={(block) => exec("formatBlock", `<${block}>`)}
          />
        </div>
        <span
          aria-hidden="true"
          className="mx-0.5 h-5 w-px flex-shrink-0 bg-line"
        />

        {markButton(
          <Bold className="h-[15px] w-[15px]" />,
          "Bold",
          formatting.bold,
          () => exec("bold")
        )}
        {markButton(
          <Italic className="h-[15px] w-[15px]" />,
          "Italic",
          formatting.italic,
          () => exec("italic")
        )}
        {markButton(
          <Underline className="h-[15px] w-[15px]" />,
          "Underline",
          formatting.underline,
          () => exec("underline")
        )}
        <span
          aria-hidden="true"
          className="mx-0.5 h-5 w-px flex-shrink-0 bg-line"
        />

        {markButton(
          <List className="h-[15px] w-[15px]" />,
          "Bulleted list",
          formatting.bullets,
          () => exec("insertUnorderedList")
        )}
        {markButton(
          <ListOrdered className="h-[15px] w-[15px]" />,
          "Numbered list",
          formatting.numbers,
          () => exec("insertOrderedList")
        )}

        <span
          aria-hidden="true"
          className="mx-0.5 h-5 w-px flex-shrink-0 bg-line"
        />

        {/* The two colours: the text's own, and the highlight behind it.
            Each button wears the colour the caret is standing in as the bar
            under its mark, and opens a palette under it. */}
        <ColourPicker
          title="Text colour"
          icon={
            <span className="text-[13px] font-semibold leading-none text-ink">
              A
            </span>
          }
          colours={COLOURS}
          current={formatting.colour}
          onPick={(colour) => exec("foreColor", colour)}
          custom
        />
        <ColourPicker
          title="Highlight"
          icon={<Highlighter className="h-[13px] w-[13px] text-ink" />}
          colours={HIGHLIGHTS}
          current={formatting.highlight}
          none="No highlight"
          // `hiliteColor` is the highlighter's own command; `backColor` is
          // what the older engines call it, and what the reading above asks.
          onPick={(colour) =>
            exec(
              document.queryCommandSupported?.("hiliteColor")
                ? "hiliteColor"
                : "backColor",
              colour
            )
          }
        />

        <span
          aria-hidden="true"
          className="mx-0.5 h-5 w-px flex-shrink-0 bg-line"
        />

        {markButton(<Undo2 className="h-[15px] w-[15px]" />, "Undo", false, () =>
          exec("undo")
        )}
        {markButton(<Redo2 className="h-[15px] w-[15px]" />, "Redo", false, () =>
          exec("redo")
        )}
      </div>

      {/* The box. Its `data-empty` is what the stylesheet draws the
          placeholder off - a `contenteditable` is rarely truly `:empty`,
          since the browser leaves a `<br>` behind the last deleted word. */}
      <div
        ref={editorRef}
        id={id}
        role="textbox"
        aria-label={label}
        aria-multiline="true"
        contentEditable
        suppressContentEditableWarning
        data-empty={empty || undefined}
        data-placeholder={placeholder}
        onInput={emit}
        onPaste={handlePaste}
        onFocus={handleFocus}
        // A wider margin either side than the bar above has, so the text
        // sits in from the frame the way it will on a page. Not on a phone:
        // 48px a side of a 360px screen is a third of the width, spent on a
        // margin that is only standing in for a printed one, and it leaves
        // list items wrapping after four words. The page's own gutter there.
        // It grows with what is written on a phone and lets the panel do
        // the scrolling, rather than being a box that scrolls inside a screen
        // that also scrolls: below `md` the shell is the one scroller on every
        // screen in this app, and two of them a thumb-width apart is a page
        // that moves the wrong thing half the time. From `md` it is a card of
        // its own again, with its own scrollbar.
        //
        // Whatever reads this document back is set in by the same amounts, so
        // pressing Edit puts a box around the words rather than moving them.
        className={`rich-text rich-text--editor flex-1 overflow-y-auto px-5 py-5 text-ink focus:outline-none custom-scrollbar-overlay max-md:flex-none max-md:overflow-visible md:px-12 md:py-6 ${contentClassName}`}
      />

      {footer}
    </div>
  );
};

export default RichTextEditor;
